import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFlags } from "launchdarkly-react-client-sdk";
import { chatApiInstance } from "../apis/chatApi";
import {
  buildTicketQualityReviewContext,
  type TicketQualityReviewPayload,
} from "../utils/ticketAiOverviewContext";
import { isTicketQualityReviewLdEnabled } from "../utils/ticketQualityReviewAccess";

export interface TicketQualityReviewResult {
  summary: string;
  positives: string[];
  issues: string[];
}

interface TicketQualityReviewActionProps {
  ticketId: string;
  /** Full ticket thread payload ({ ticket, conversations, notes }) used to build review context. */
  payload: TicketQualityReviewPayload;
  customerId?: number | string | null;
}

/**
 * "QA review" button rendered on every supervisor ticket, gated by the
 * LaunchDarkly `qa-review` flag. Clicking it opens a popup dialog with the
 * on-demand review (summary, positives, issues to improve). Nothing is
 * stored in the DB.
 */
const TicketQualityReviewAction: React.FC<TicketQualityReviewActionProps> = ({
  ticketId,
  payload,
  customerId = null,
}) => {
  const flags = useFlags();
  const enabled = isTicketQualityReviewLdEnabled(flags);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<TicketQualityReviewResult | null>(null);

  const runReview = useCallback(async () => {
    const context = buildTicketQualityReviewContext(payload);
    if (!context.trim()) {
      setError("No ticket thread loaded yet.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await chatApiInstance.fetchTicketQualityReview(
        ticketId,
        context,
        customerId
      );
      setReview({
        summary: String(result.summary || "").trim(),
        positives: Array.isArray(result.positives) ? result.positives : [],
        issues: Array.isArray(result.issues) ? result.issues : [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run QA review.");
    } finally {
      setIsLoading(false);
    }
  }, [ticketId, payload, customerId]);

  const handleOpen = () => {
    setOpen(true);
    setReview(null);
    setError(null);
    void runReview();
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!enabled) {
    return null;
  }

  return (
    <>
      <Button variant="outlined" size="small" onClick={handleOpen}>
        QA review
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 700,
            fontSize: "16px",
            color: "#172033",
          }}
        >
          Quality review — ticket {ticketId}
          <IconButton onClick={handleClose} size="small" aria-label="Close QA review">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Reviewing resolution accuracy, root cause, and remediation…
              </Typography>
            </Box>
          )}
          {error && !isLoading && (
            <Typography variant="body2" sx={{ color: "error.main", py: 1 }}>
              {error}
            </Typography>
          )}
          {review && !isLoading && !error && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {review.summary && (
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "14px", color: "#172033" }}
                >
                  {review.summary}
                </Typography>
              )}
              {review.positives.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: "13px", color: "#2E7D32", mb: 0.5 }}
                  >
                    Positives
                  </Typography>
                  {review.positives.map((item, index) => (
                    <Typography
                      key={`positive-${index}`}
                      variant="body2"
                      sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "13px", color: "#334155", mb: 0.5 }}
                    >
                      • {item}
                    </Typography>
                  ))}
                </Box>
              )}
              {review.issues.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: "13px", color: "#C62828", mb: 0.5 }}
                  >
                    Issues to improve
                  </Typography>
                  {review.issues.map((item, index) => (
                    <Typography
                      key={`issue-${index}`}
                      variant="body2"
                      sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "13px", color: "#334155", mb: 0.5 }}
                    >
                      • {item}
                    </Typography>
                  ))}
                </Box>
              )}
              {review.positives.length === 0 && review.issues.length === 0 && !review.summary && (
                <Typography variant="body2" color="text.secondary">
                  No review content returned.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void runReview()} disabled={isLoading}>
            Re-run
          </Button>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketQualityReviewAction;
